import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMarketingPageById, getImageUrl, getCampaignVouchers } from '../services/api';
import { getRedirectUrl } from '../utils/tokenManager';
import { resolveVioAssetUrl } from '../utils/vioAssetUrl';
import { getDateLocale } from '../i18n';
import './Home.css';

// Member app: /vouchers/:voucherId → VoucherClaimPage → GET /api/vouchers/:voucherId (voucher template; marketing list uses this id)
//           /voucher/:id  → VoucherDetailPage → GET /api/vouchers/me/:userVoucherId (claimed instance only)
const VOUCHER_TEMPLATE_SEGMENT = 'vouchers';

const isVoucherListItemNotExpired = (item) => {
  if (!item || typeof item !== 'object') return false;
  const end = item.voucherSchema?.expiryEndDatetime;
  if (end == null || end === '') return true;
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(endMs)) return true;
  return endMs >= Date.now();
};

const isVoucherSchemaActive = (item) => {
  if (!item || typeof item !== 'object') return false;
  const vs = item.voucherSchema || {};
  const status = vs.status ?? item.status;
  if (typeof status === 'string' && status.trim().toLowerCase() === 'inactive') {
    return false;
  }
  if (vs.isActive === false || item.isActive === false) {
    return false;
  }
  return true;
};

const isVoucherListItemDisplayable = (item) =>
  isVoucherListItemNotExpired(item) && isVoucherSchemaActive(item);

// Parse the tail date of an ops-side validPeriod string like "DD/MM/YYYY - DD/MM/YYYY".
// Returns the end-of-day timestamp (ms) so the voucher is valid through the whole expiry day.
// Returns null if the string is missing, dashed ("-"), or unparseable.
const parseValidPeriodEnd = (validPeriod) => {
  if (!validPeriod || typeof validPeriod !== 'string') return null;
  const parts = validPeriod.split(/\s*[-–]\s*/);
  const endStr = parts[parts.length - 1]?.trim();
  if (!endStr) return null;
  const match = endStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const endOfDay = new Date(
    Number(yyyy),
    Number(mm) - 1,
    Number(dd),
    23, 59, 59, 999
  ).getTime();
  return Number.isFinite(endOfDay) ? endOfDay : null;
};

// Expiry check for vouchers shown in the marketing page lists (Link Voucher + Campaign).
// Prefers a machine-readable ISO field (expiryEndDatetime); falls back to parsing the
// ops-side formatted validPeriod for older saved marketing pages that don't carry the ISO.
// When no expiry info exists at all, the voucher is treated as non-expiring (kept visible).
const isMarketingVoucherNotExpired = (voucher) => {
  if (!voucher || typeof voucher !== 'object') return false;
  const iso =
    voucher.expiryEndDatetime ||
    voucher.voucherSchema?.expiryEndDatetime ||
    '';
  if (iso) {
    const isoMs = new Date(iso).getTime();
    if (Number.isFinite(isoMs)) return isoMs >= Date.now();
  }
  const fallbackMs = parseValidPeriodEnd(voucher.validPeriod);
  if (fallbackMs != null) return fallbackMs >= Date.now();
  return true;
};

const isMarketingVoucherActive = (voucher) => {
  if (!voucher || typeof voucher !== 'object') return false;
  const status = voucher.status || voucher.voucherSchema?.status;
  if (typeof status === 'string' && status.trim().toLowerCase() === 'inactive') {
    return false;
  }
  if (voucher.isActive === false || voucher.voucherSchema?.isActive === false) {
    return false;
  }
  return true;
};

const isMarketingVoucherDisplayable = (voucher) =>
  isMarketingVoucherNotExpired(voucher) && isMarketingVoucherActive(voucher);

function MarketingPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [campaignVouchersData, setCampaignVouchersData] = useState([]);
  const [campaignVouchersLoading, setCampaignVouchersLoading] = useState(false);
  const carouselRef = useRef(null);
  const lastClickedBannerIndexRef = useRef(null);
  const restoreTransitionRef = useRef(null);

  const getVoucherTokenInfo = (voucher = {}) => {
    const token = voucher.token || voucher.claimCostToken || {};
    const tokenIconRaw =
      voucher.tokenIcon ||
      token.logo?.imageUrl ||
      token.icon ||
      token.image ||
      token.imageUrl ||
      token.logoUrl ||
      '';
    const tokenSymbol = voucher.tokenSymbol || token.symbol || token.tokenSymbol || '';
    const tokenName = voucher.tokenName || token.name || token.tokenName || tokenSymbol || t('common.token');

    return {
      tokenName,
      tokenSymbol: tokenName || tokenSymbol || t('common.token'),
      tokenIconRaw,
    };
  };

  // Normalize banner voucher into the same shape as transformVouchers so that
  // getVoucherTemplateUrl can emit the full query (campaignId + token info),
  // matching how Link Voucher / Hot Offers cards build their URLs.
  const getBannerVoucherData = (banner = {}) => {
    const data = banner.selectedVoucherData || {};
    const id = data.id || banner.selectedVoucher || '';
    const voucherSchemaId =
      data.voucherSchemaId || data.id || banner.selectedVoucher || '';
    const assetBase = pageData?.vioAssetBaseUrl || '';
    const tokenInfo = getVoucherTokenInfo(data);
    const resolvedTokenIcon = tokenInfo.tokenIconRaw
      ? resolveVioAssetUrl(tokenInfo.tokenIconRaw, assetBase) || ''
      : '';
    const tokens =
      data.price !== undefined && data.price !== null ? data.price : 0;

    return {
      ...data,
      id,
      voucherSchemaId,
      campaignId: data.campaignId || '',
      tokens,
      tokenName: tokenInfo.tokenName,
      tokenSymbol: tokenInfo.tokenSymbol,
      tokenIcon: resolvedTokenIcon,
    };
  };

  // Fetch marketing page data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getMarketingPageById(id);
        setPageData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching marketing page:', err);
        setError('marketing.notFound');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Auto-slide banner - infinite loop carousel (paused when tab hidden so same banner shows when user returns)
  const [isTransitioning, setIsTransitioning] = useState(true);
  const bannerCount = pageData?.fullConfig?.banners?.length || 0;
  
  useEffect(() => {
    if (bannerCount <= 1) return;
    const interval = setInterval(() => {
      if (document.hidden) return;
      setCurrentBanner((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [bannerCount]);

  // When user returns to PWA tab, show the banner they clicked (snap without transition so it looks clean)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && bannerCount > 0) {
        const saved = lastClickedBannerIndexRef.current;
        if (saved !== null && saved < bannerCount) {
          lastClickedBannerIndexRef.current = null;
          if (restoreTransitionRef.current) clearTimeout(restoreTransitionRef.current);
          setIsTransitioning(false);
          setCurrentBanner(saved);
          restoreTransitionRef.current = setTimeout(() => {
            setIsTransitioning(true);
            restoreTransitionRef.current = null;
          }, 50);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (restoreTransitionRef.current) clearTimeout(restoreTransitionRef.current);
    };
  }, [bannerCount]);
  
  // Handle infinite loop - when reaching cloned slide, jump to real slide
  useEffect(() => {
    if (bannerCount > 1 && currentBanner >= bannerCount) {
      // Disable transition, jump to first real slide
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentBanner(0);
      }, 500); // Wait for transition to complete
      
      return () => clearTimeout(timeout);
    }
  }, [currentBanner, bannerCount]);
  
  // Re-enable transition after jump
  useEffect(() => {
    if (!isTransitioning) {
      const timeout = setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [isTransitioning]);

  // Fetch campaign vouchers when campaign is enabled
  useEffect(() => {
    const fetchCampaignVouchers = async () => {
      if (!pageData?.fullConfig?.content?.linkCampaignEnabled || !pageData?.fullConfig?.content?.selectedCampaign) {
        return;
      }

      const campaignId = pageData.fullConfig.content.selectedCampaign;
      if (!campaignId || campaignId === '-') {
        return;
      }

      try {
        setCampaignVouchersLoading(true);
        const response = await getCampaignVouchers(campaignId, 1, 100000, pageData.tenantId);
        
        // Transform API response to match our voucher structure
        if (response?.message?.data && Array.isArray(response.message.data)) {
          const assetBase = pageData.vioAssetBaseUrl || '';
          const transformed = response.message.data.filter(isVoucherListItemDisplayable).map((item) => {
            const vs = item.voucherSchema || {};
            const token = item.token || {};
            const startDate = vs.expiryStartDatetime ? new Date(vs.expiryStartDatetime).toLocaleDateString('en-GB') : '';
            const endDate = vs.expiryEndDatetime ? new Date(vs.expiryEndDatetime).toLocaleDateString('en-GB') : '';
            const validPeriod = startDate && endDate ? `${startDate} - ${endDate}` : '-';

            const imageUrlRaw = vs.image?.imageUrl || '';
            const imageUrl = resolveVioAssetUrl(imageUrlRaw, assetBase) || '';
            const tokenLogoRaw =
              token.logo?.imageUrl ||
              token.icon ||
              token.image ||
              token.imageUrl ||
              token.logoUrl ||
              '';
            const tokenIconResolved = tokenLogoRaw
              ? resolveVioAssetUrl(tokenLogoRaw, assetBase) || '/images/Quill token.png'
              : '/images/Quill token.png';

            // Display fallbacks ('Token' / 'Company') are applied at render time so they follow the language.
            const tokenSymbol = token.symbol || token.tokenSymbol || '';
            const tokenName = token.name || token.tokenName || tokenSymbol;
            const companyName =
              vs.company?.companyName ||
              item.brandList?.[0]?.brandName ||
              vs.brands?.[0]?.brandName ||
              '';
            const companyLogoRaw =
              vs.company?.logo?.imageUrl ||
              item.brandList?.[0]?.logo?.imageUrl ||
              item.brandList?.[0]?.logo ||
              vs.brands?.[0]?.logo?.imageUrl ||
              vs.brands?.[0]?.logo ||
              '';
            const companyIconResolved = companyLogoRaw
              ? resolveVioAssetUrl(companyLogoRaw, assetBase) || ''
              : '';

            return {
              id: item.campaignVoucherSchemaId,
              voucherSchemaId: item.voucherSchemaId || vs.voucherSchemaId || item.campaignVoucherSchemaId,
              campaignId: item.campaignId || campaignId || '',
              campaignVoucherId: item.campaignVoucherId || '',
              name: vs.voucherSchemaName?.en || '-',
              type: vs.voucherType || '-',
              price: item.price || 0,
              imageUrl,
              validPeriod: validPeriod,
              tokenName,
              tokenSymbol: tokenName || tokenSymbol,
              tokenIcon: tokenIconResolved,
              companyName,
              companyIcon: companyIconResolved,
            };
          });
          
          setCampaignVouchersData(transformed);
        } else {
          const reason = response?._debug || (response?.message?.data ? null : 'empty');
          if (reason) console.warn('Campaign vouchers empty:', reason, response?._error || '');
          setCampaignVouchersData([]);
        }
      } catch (err) {
        console.error('Error fetching campaign vouchers:', err);
        setCampaignVouchersData([]);
      } finally {
        setCampaignVouchersLoading(false);
      }
    };

    if (pageData) {
      fetchCampaignVouchers();
    }
  }, [pageData]);

  // Link vouchers use stored data directly (no API call needed - data is saved when creating marketing page)
  // This avoids needing operator credentials and works with vouchers from multiple campaigns

  // Fixed slogan font size - no dynamic adjustment

  // Handle touch events for swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!pageData?.fullConfig?.banners) return;
    const bannerCount = pageData.fullConfig.banners.length;
    
    if (touchStart - touchEnd > 75) {
      // Swipe left
      setCurrentBanner((prev) => (prev + 1) % bannerCount);
    }
    if (touchStart - touchEnd < -75) {
      // Swipe right
      setCurrentBanner((prev) => (prev - 1 + bannerCount) % bannerCount);
    }
  };

  // Handle banner click - save which banner was clicked so we can show it again when user returns
  const handleBannerClick = (banner) => {
    const banners = pageData?.fullConfig?.banners;
    if (banners?.length) {
      const index = banners.findIndex((b) => (b.id && b.id === banner.id) || b === banner);
      if (index >= 0) lastClickedBannerIndexRef.current = index;
    }
    if (banner.linkType === 'custom' && banner.link) {
      window.open(banner.link, '_blank');
    } else if (banner.linkType === 'voucher' && banner.selectedVoucher) {
      const voucher = getBannerVoucherData(banner);
      if (!isMarketingVoucherActive(voucher)) return;
      const voucherUrl = getVoucherTemplateUrl(voucher.voucherSchemaId || voucher.id, voucher.campaignId, voucher);
      if (voucherUrl) {
        window.open(voucherUrl, '_blank');
      }
    }
  };

  // Transform vouchers to match Home.js structure
  const transformVouchers = (vouchers) => {
    if (!vouchers || vouchers.length === 0) return [];
    const assetBase = pageData?.vioAssetBaseUrl || '';
    const fallbackImg =
      'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=400&fit=crop';

    return vouchers.filter(isMarketingVoucherDisplayable).map((voucher, index) => {
      const resolvedImg = resolveVioAssetUrl(voucher.imageUrl, assetBase);
      const tokenInfo = getVoucherTokenInfo(voucher);
      const resolvedToken = tokenInfo.tokenIconRaw
        ? resolveVioAssetUrl(tokenInfo.tokenIconRaw, assetBase)
        : null;
      return {
        id: voucher.id || index,
        voucherSchemaId: voucher.voucherSchemaId || voucher.voucherSchema?.voucherSchemaId || voucher.id || '',
        campaignId: voucher.campaignId || '',
        title: voucher.name || t('common.voucher'),
        image: resolvedImg || fallbackImg,
        tokens: voucher.price || 0,
        tokenName: tokenInfo.tokenName,
        tokenSymbol: tokenInfo.tokenSymbol,
        tokenIcon: resolvedToken || '/images/Quill token.png',
        companyName: voucher.companyName || t('common.company'),
        companyIcon: voucher.companyIcon ? resolveVioAssetUrl(voucher.companyIcon, assetBase) || '' : '',
      };
    });
  };

  // Get voucher grid class based on position and total count
  // Uses 2-column grid with row spans for irregular layout
  // Grid positions: 1,2 / 3,4 / 5,6 / 7,8 (left to right, top to bottom)
  // 
  // Pattern:
  // - 1 voucher: V1 uses 1,2,3,4 (full width)
  // - 2 vouchers: V1 uses 1,3 (tall), V2 uses 2,4 (tall)
  // - 3 vouchers: V1 uses 1 (short), V2 uses 2,4 (tall), V3 uses 3 (short)
  // - 4 vouchers: V1 uses 1 (short), V2 uses 2,4 (tall), V3 uses 3,5 (tall), V4 uses 6 (short)
  // - 5 vouchers: V1 uses 1 (short), V2 uses 2,4 (tall), V3 uses 3,5 (tall), V4 uses 6,8 (tall), V5 uses 7 (short)
  // - 6+ vouchers: Groups of 5, remainder uses pattern for that count
  //   e.g., 7 vouchers = 5-pattern + 2-pattern (tall, tall)
  const getVoucherGridClass = (index, total) => {
    // For totals <= 5, use direct pattern
    if (total <= 5) {
      return getPatternForCount(index, total);
    }
    
    // For 6+ vouchers, split into groups of 5 + remainder
    const groupOf5Count = Math.floor(total / 5);
    const remainder = total % 5;
    const fullGroupsVouchers = groupOf5Count * 5;
    
    if (remainder === 0) {
      // Exact multiple of 5, all groups use 5-pattern
      const posInGroup = index % 5;
      return getPatternForCount(posInGroup, 5);
    } else {
      // Has remainder
      if (index < fullGroupsVouchers) {
        // In a full group of 5
        const posInGroup = index % 5;
        return getPatternForCount(posInGroup, 5);
      } else {
        // In the remainder group
        const posInRemainder = index - fullGroupsVouchers;
        return getPatternForCount(posInRemainder, remainder);
      }
    }
  };
  
  // Helper function to get pattern for a specific count
  const getPatternForCount = (posInGroup, groupSize) => {
    if (groupSize === 1) {
      return 'voucher-full-width';
    } else if (groupSize === 2) {
      return 'voucher-tall'; // Both tall
    } else if (groupSize === 3) {
      // short, tall, short
      if (posInGroup === 1) return 'voucher-tall';
      return 'voucher-short';
    } else if (groupSize === 4) {
      // short, tall, tall, short
      if (posInGroup === 0 || posInGroup === 3) return 'voucher-short';
      return 'voucher-tall';
    } else {
      // 5 vouchers: short, tall, tall, tall, short
      if (posInGroup === 0 || posInGroup === 4) return 'voucher-short';
      return 'voucher-tall';
    }
  };

  // Base for VIO member PWA links.
  // - When opened from a VIO miniapp, redirectUrl already includes the tenant slug
  //   (e.g. "https://app.vio.com/acme"), so use it as-is.
  // - When opened directly (shared link / standalone marketing page), fall back to
  //   the env-configured vioPwaBaseUrl and append the tenant slug ourselves so the
  //   VIO PWA resolves the correct tenant context (login page, branding, defaults).
  const getVioPwaBase = () => {
    const redirect = getRedirectUrl();
    if (redirect) return redirect.replace(/\/$/, '');

    const base = pageData?.vioPwaBaseUrl;
    if (!base) return null;
    const trimmed = base.replace(/\/$/, '');
    const slug = pageData?.tenantSlug;
    if (!slug) return trimmed;
    return `${trimmed}/${encodeURIComponent(slug)}`;
  };

  /**
   * Template / claim page (matches list ids: campaignVoucherSchemaId → voucher definition id).
   * Optional campaignId so VoucherClaimPage can claim within that campaign.
   */
  const getVoucherTemplateUrl = (voucherId, campaignId, voucher = {}) => {
    const base = getVioPwaBase();
    if (!base || !voucherId) return null;
    const trimmed = base.replace(/\/$/, '');
    const params = new URLSearchParams();
    if (campaignId && campaignId !== '-') {
      params.set('campaignId', campaignId);
    }
    if (voucher.tokens !== undefined && voucher.tokens !== null) {
      params.set('tokenAmount', String(voucher.tokens));
    }
    if (voucher.tokenName) params.set('tokenName', voucher.tokenName);
    if (voucher.tokenSymbol) params.set('tokenSymbol', voucher.tokenSymbol);
    if (voucher.tokenIcon && voucher.tokenIcon !== '/images/Quill token.png') {
      params.set('tokenIcon', voucher.tokenIcon);
    }

    const queryString = params.toString();
    let path = `${trimmed}/${VOUCHER_TEMPLATE_SEGMENT}/${encodeURIComponent(voucherId)}`;
    if (queryString) path += `?${queryString}`;
    return path;
  };

  // VIO PWA login page URL (same base as voucher links)
  const getLoginUrl = () => {
    const base = getVioPwaBase();
    if (!base) return null;
    const trimmed = base.replace(/\/$/, '');
    return `${trimmed}/login`;
  };

  // Transform vouchers for Hot Offers section (featured + scrollable layout)
  const transformVouchersForHotOffers = (vouchers) => {
    if (!vouchers || vouchers.length === 0) return { featured: [], scrollable: [] };
    const assetBase = pageData?.vioAssetBaseUrl || '';
    const fallbackImg =
      'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=400&fit=crop';

    // Safety net: campaign vouchers are already filtered at fetch time via
    // isVoucherListItemNotExpired, but re-check here in case any expired item
    // sneaks through (e.g. cached state, mid-day expiry between fetch and render).
    const transformed = vouchers.filter(isMarketingVoucherDisplayable).map((voucher, index) => {
      const resolvedImg = resolveVioAssetUrl(voucher.imageUrl, assetBase);
      const tokenInfo = getVoucherTokenInfo(voucher);
      const resolvedToken = tokenInfo.tokenIconRaw
        ? resolveVioAssetUrl(tokenInfo.tokenIconRaw, assetBase)
        : null;
      return {
        id: voucher.id || index,
        title: voucher.name || t('common.voucher'),
        image: resolvedImg || fallbackImg,
        tokens: voucher.price || 0,
        tokenName: tokenInfo.tokenName,
        tokenSymbol: tokenInfo.tokenSymbol,
        tokenIcon: resolvedToken || '/images/Quill token.png',
        campaignId: voucher.campaignId || '',
        voucherSchemaId: voucher.voucherSchemaId || voucher.voucherSchema?.voucherSchemaId || voucher.id || '',
        companyName: voucher.companyName || t('common.company'),
        companyIcon: voucher.companyIcon ? resolveVioAssetUrl(voucher.companyIcon, assetBase) || '' : '',
      };
    });
    
    return {
      featured: transformed.slice(0, 3),
      scrollable: transformed.slice(3)
    };
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e2e8f0',
          borderTopColor: '#00364F',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>{t('marketing.notFoundTitle')}</h2>
        <p style={{ color: '#64748b' }}>{t(error || 'marketing.notFoundBody')}</p>
      </div>
    );
  }

  // Check if page is hidden
  if (pageData.hidden) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div style={{
          fontSize: '4rem',
          marginBottom: '1rem'
        }}>🔒</div>
        <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>{t('marketing.unavailableTitle')}</h2>
        <p style={{ color: '#64748b' }}>{t('marketing.unavailableBody')}</p>
      </div>
    );
  }

  const { fullConfig } = pageData;
  const { basic, banners, content } = fullConfig;
  
  // Transform vouchers for Link Voucher section (uses stored data from marketing page config)
  const voucherSectionVouchers = content.linkVoucherEnabled && content.vouchers?.length > 0
    ? transformVouchers(content.vouchers)
    : [];
  
  // Transform campaign vouchers for Hot Offers section
  const campaignVouchers = transformVouchersForHotOffers(campaignVouchersData);
  
  const hotOffersCampaignId =
    content.linkCampaignEnabled && content.selectedCampaign && content.selectedCampaign !== '-'
      ? content.selectedCampaign
      : null;

  return (
    <div className="home-page">
      {/* Logo Section */}
      <header className="logo-header">
        <div className="logo-wrapper">
          {logoError || !basic.companyLogoUrl ? (
            <div className="logo-placeholder">
              <span className="logo-text-fallback">{basic.companyLogoName || t('common.company')}</span>
            </div>
          ) : (
            <img 
              src={getImageUrl(basic.companyLogoUrl)} 
              alt={basic.companyLogoName || t('common.companyLogo')} 
              className="logo-img"
              onError={() => setLogoError(true)}
              onLoad={() => setLogoError(false)}
            />
          )}
        </div>
      </header>

      {/* Carousel Banner with Slogan */}
      {banners && banners.length > 0 && (
        <section className="banner-section">
          <div 
            className="carousel-container"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="carousel-track"
              style={{ 
                transform: `translateX(-${currentBanner * 100}%)`,
                transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none'
              }}
            >
              {banners.map((banner, index) => (
                <div 
                  key={banner.id || index} 
                  className="carousel-slide"
                  onClick={() => handleBannerClick(banner)}
                >
                  <img src={getImageUrl(banner.imageUrl)} alt={banner.title || t('common.banner')} />
                </div>
              ))}
              {/* Clone first slide for seamless loop */}
              {banners.length > 1 && (
                <div 
                  className="carousel-slide"
                  onClick={() => handleBannerClick(banners[0])}
                >
                  <img src={getImageUrl(banners[0].imageUrl)} alt={banners[0].title || t('common.banner')} />
                </div>
              )}
            </div>
          </div>

          {/* Slogan */}
          {basic.slogan && (
            <div className="slogan">
              <h1>{basic.slogan}</h1>
            </div>
          )}
        </section>
      )}

      {/* Link Voucher Section */}
      {content.linkVoucherEnabled && voucherSectionVouchers.length > 0 && (
        <section className="voucher-section">
          <h2 className="section-title">{content.linkVoucherTitle || t('marketing.vouchersTitle')}</h2>
          <div className="voucher-grid">
            {voucherSectionVouchers.map((voucher, index) => {
              const url = getVoucherTemplateUrl(voucher.voucherSchemaId || voucher.id, voucher.campaignId, voucher);
              const gridClass = getVoucherGridClass(index, voucherSectionVouchers.length);
              const card = (
                <div className={`voucher-card ${gridClass}`}>
                  <div className="voucher-image">
                    <img src={voucher.image} alt={voucher.title} className="voucher-main-img" />
                    <div className="voucher-info-overlay">
                      <span className="voucher-title">{voucher.title}</span>
                      <div className="voucher-tokens">
                        <img src={voucher.tokenIcon} alt={voucher.tokenSymbol} className="token-img" />
                        <span>{voucher.tokenSymbol}</span>
                        <span className="token-amount">{voucher.tokens === 0 ? t('common.free') : voucher.tokens.toLocaleString(getDateLocale())}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
              return (
                <React.Fragment key={voucher.id}>
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className={`voucher-card-link ${gridClass}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>{card}</a>
                  ) : (
                    card
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>
      )}

      {/* Link Campaign Section (Hot Offers style) */}
      {content.linkCampaignEnabled && (
        <section className="voucher-section hot-offers">
          <h2 className="section-title">{content.linkCampaignTitle || t('marketing.campaignTitle')}</h2>
          
          <div className="hot-offers-container">
            {campaignVouchersLoading && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.8)' }}>{t('marketing.loadingVouchers')}</div>
            )}
            {!campaignVouchersLoading && campaignVouchersData.length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>{t('marketing.noCampaignVouchers')}</div>
            )}
            
            {/* Campaign Vouchers Grid Layout */}
            {campaignVouchers.featured.length > 0 && (
              <div className="campaign-voucher-grid">
                {campaignVouchers.featured.map((voucher, index) => {
                  const url = getVoucherTemplateUrl(voucher.voucherSchemaId || voucher.id, hotOffersCampaignId, voucher);
                  const total = campaignVouchers.featured.length;
                  
                  // Get grid class based on position and total
                  // 1 voucher: full width
                  // 2 vouchers: both tall
                  // 3 vouchers: 1 tall + 2 short
                  let gridClass = 'campaign-voucher-short';
                  if (total === 1) {
                    gridClass = 'campaign-voucher-full';
                  } else if (total === 2) {
                    gridClass = 'campaign-voucher-tall';
                  } else if (total >= 3) {
                    // 3 vouchers: first is tall, others are short
                    gridClass = index === 0 ? 'campaign-voucher-tall' : 'campaign-voucher-short';
                  }
                  
                  const card = (
                    <div className={`voucher-card ${gridClass}`}>
                      <div className="voucher-image">
                        <img src={voucher.image} alt={voucher.title} className="voucher-main-img" />
                        <div className="voucher-info-overlay">
                          <span className="voucher-title">{voucher.title}</span>
                          <div className="voucher-tokens">
                            <img src={voucher.tokenIcon} alt={voucher.tokenSymbol} className="token-img" />
                            <span>{voucher.tokenSymbol}</span>
                            <span className="token-amount">{voucher.tokens === 0 ? t('common.free') : voucher.tokens.toLocaleString(getDateLocale())}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  return (
                    <React.Fragment key={voucher.id}>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className={`campaign-voucher-link ${gridClass}`} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>{card}</a>
                      ) : (
                        card
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Horizontal Scrollable Vouchers for 4+ */}
            {campaignVouchers.scrollable.length > 0 && (
              <div className="scrollable-vouchers">
                {campaignVouchers.scrollable.map((voucher) => {
                  const url = getVoucherTemplateUrl(voucher.voucherSchemaId || voucher.id, hotOffersCampaignId, voucher);
                  const card = (
                    <div className="small-voucher-card">
                      <div className="small-voucher-image">
                        <img src={voucher.image} alt={voucher.title} />
                        <div className="small-voucher-badge">
                          <div className="badge-icon">
                            {voucher.companyIcon ? (
                              <img src={voucher.companyIcon} alt={voucher.companyName || t('common.company')} className="badge-icon-img" />
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                          <span>{voucher.companyName || t('common.company')}</span>
                        </div>
                      </div>
                      <div className="small-voucher-info">
                        <span className="small-voucher-title">{voucher.title}</span>
                        <div className="small-voucher-tokens">
                          <img src={voucher.tokenIcon} alt={voucher.tokenSymbol} className="token-img-small" />
                          <span className="token-label">{voucher.tokenSymbol}</span>
                          <span className="token-value">{voucher.tokens === 0 ? t('common.free') : voucher.tokens}</span>
                        </div>
                      </div>
                    </div>
                  );
                  return (
                    <React.Fragment key={voucher.id}>
                      {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>{card}</a> : card}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Button - links to VIO PWA login when vioPwaBaseUrl is set */}
      <div className="cta-container">
        {getLoginUrl() ? (
          <a href={getLoginUrl()} target="_blank" rel="noopener noreferrer" className="cta-button">
            {t('marketing.discoverMore')}
          </a>
        ) : (
          <button className="cta-button">{t('marketing.discoverMore')}</button>
        )}
      </div>
    </div>
  );
}

export default MarketingPage;
